import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Table } from 'reactstrap';
import { JhiItemCount, JhiPagination, Translate, getPaginationState } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { ASC, DESC, ITEMS_PER_PAGE, SORT } from 'app/shared/util/pagination.constants';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities } from './central-heating.reducer';

export const CentralHeating = () => {
  const dispatch = useAppDispatch();

  const pageLocation = useLocation();
  const navigate = useNavigate();

  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getPaginationState(pageLocation, ITEMS_PER_PAGE, 'id'), pageLocation.search),
  );

  const centralHeatingList = useAppSelector(state => state.centralHeating.entities);
  const loading = useAppSelector(state => state.centralHeating.loading);
  const totalItems = useAppSelector(state => state.centralHeating.totalItems);

  const getAllEntities = () => {
    dispatch(
      getEntities({
        page: paginationState.activePage - 1,
        size: paginationState.itemsPerPage,
        sort: `${paginationState.sort},${paginationState.order}`,
      }),
    );
  };

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort]);

  useEffect(() => {
    const params = new URLSearchParams(pageLocation.search);
    const page = params.get('page');
    const sort = params.get(SORT);
    if (page && sort) {
      const sortSplit = sort.split(',');
      setPaginationState({
        ...paginationState,
        activePage: +page,
        sort: sortSplit[0],
        order: sortSplit[1],
      });
    }
  }, [pageLocation.search]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = currentPage =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = paginationState.sort;
    const order = paginationState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    }
    return order === ASC ? faSortUp : faSortDown;
  };

  return (
    <div>
      <h2 id="central-heating-heading" data-cy="CentralHeatingHeading">
        <Translate contentKey="apiLocationApp.centralHeating.home.title">Central Heatings</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2" color="info" onClick={handleSyncList} disabled={loading}>
            <FontAwesomeIcon icon="sync" spin={loading} />{' '}
            <Translate contentKey="apiLocationApp.centralHeating.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Link to="/central-heating/new" className="btn btn-primary jh-create-entity" id="jh-create-entity" data-cy="entityCreateButton">
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            <Translate contentKey="apiLocationApp.centralHeating.home.createLabel">Create new Central Heating</Translate>
          </Link>
        </div>
      </h2>
      <div className="table-responsive">
        {centralHeatingList && centralHeatingList.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th className="hand" onClick={sort('id')}>
                  <Translate contentKey="apiLocationApp.centralHeating.id">ID</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                </th>
                <th className="hand" onClick={sort('geographicLevel')}>
                  <Translate contentKey="apiLocationApp.centralHeating.geographicLevel">Geographic Level</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('geographicLevel')} />
                </th>
                <th className="hand" onClick={sort('date')}>
                  <Translate contentKey="apiLocationApp.centralHeating.date">Date</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('date')} />
                </th>
                <th className="hand" onClick={sort('geography')}>
                  <Translate contentKey="apiLocationApp.centralHeating.geography">Geography</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('geography')} />
                </th>
                <th className="hand" onClick={sort('geographyCode')}>
                  <Translate contentKey="apiLocationApp.centralHeating.geographyCode">Geography Code</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('geographyCode')} />
                </th>
                <th className="hand" onClick={sort('totalAllHouseholds')}>
                  <Translate contentKey="apiLocationApp.centralHeating.totalAllHouseholds">Total All Households</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('totalAllHouseholds')} />
                </th>
                <th className="hand" onClick={sort('gasCentralHeating')}>
                  <Translate contentKey="apiLocationApp.centralHeating.gasCentralHeating">Gas Central Heating</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('gasCentralHeating')} />
                </th>
                <th className="hand" onClick={sort('electricCentralHeating')}>
                  <Translate contentKey="apiLocationApp.centralHeating.electricCentralHeating">Electric Central Heating</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('electricCentralHeating')} />
                </th>
                <th className="hand" onClick={sort('oilCentralHeating')}>
                  <Translate contentKey="apiLocationApp.centralHeating.oilCentralHeating">Oil Central Heating</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('oilCentralHeating')} />
                </th>
                <th className="hand" onClick={sort('solidFuelCentralHeating')}>
                  <Translate contentKey="apiLocationApp.centralHeating.solidFuelCentralHeating">Solid Fuel Central Heating</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('solidFuelCentralHeating')} />
                </th>
                <th className="hand" onClick={sort('otherCentralHeating')}>
                  <Translate contentKey="apiLocationApp.centralHeating.otherCentralHeating">Other Central Heating</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('otherCentralHeating')} />
                </th>
                <th className="hand" onClick={sort('noCentralHeating')}>
                  <Translate contentKey="apiLocationApp.centralHeating.noCentralHeating">No Central Heating</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('noCentralHeating')} />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {centralHeatingList.map((centralHeating, i) => (
                <tr key={`entity-${i}`} data-cy="entityTable">
                  <td>
                    <Button tag={Link} to={`/central-heating/${centralHeating.id}`} color="link" size="sm">
                      {centralHeating.id}
                    </Button>
                  </td>
                  <td>
                    <Translate contentKey={`apiLocationApp.GeographicLevel.${centralHeating.geographicLevel}`} />
                  </td>
                  <td>{centralHeating.date}</td>
                  <td>{centralHeating.geography}</td>
                  <td>{centralHeating.geographyCode}</td>
                  <td>{centralHeating.totalAllHouseholds}</td>
                  <td>{centralHeating.gasCentralHeating}</td>
                  <td>{centralHeating.electricCentralHeating}</td>
                  <td>{centralHeating.oilCentralHeating}</td>
                  <td>{centralHeating.solidFuelCentralHeating}</td>
                  <td>{centralHeating.otherCentralHeating}</td>
                  <td>{centralHeating.noCentralHeating}</td>
                  <td className="text-end">
                    <div className="btn-group flex-btn-group-container">
                      <Button tag={Link} to={`/central-heating/${centralHeating.id}`} color="info" size="sm" data-cy="entityDetailsButton">
                        <FontAwesomeIcon icon="eye" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.view">View</Translate>
                        </span>
                      </Button>
                      <Button
                        tag={Link}
                        to={`/central-heating/${centralHeating.id}/edit?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`}
                        color="primary"
                        size="sm"
                        data-cy="entityEditButton"
                      >
                        <FontAwesomeIcon icon="pencil-alt" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.edit">Edit</Translate>
                        </span>
                      </Button>
                      <Button
                        onClick={() =>
                          (window.location.href = `/central-heating/${centralHeating.id}/delete?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`)
                        }
                        color="danger"
                        size="sm"
                        data-cy="entityDeleteButton"
                      >
                        <FontAwesomeIcon icon="trash" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.delete">Delete</Translate>
                        </span>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="apiLocationApp.centralHeating.home.notFound">No Central Heatings found</Translate>
            </div>
          )
        )}
      </div>
      {totalItems ? (
        <div className={centralHeatingList && centralHeatingList.length > 0 ? '' : 'd-none'}>
          <div className="justify-content-center d-flex">
            <JhiItemCount page={paginationState.activePage} total={totalItems} itemsPerPage={paginationState.itemsPerPage} i18nEnabled />
          </div>
          <div className="justify-content-center d-flex">
            <JhiPagination
              activePage={paginationState.activePage}
              onSelect={handlePagination}
              maxButtons={5}
              itemsPerPage={paginationState.itemsPerPage}
              totalItems={totalItems}
            />
          </div>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default CentralHeating;
